import React from 'react';
import { GenericTableColumn } from '@ui-components/Table/GenericTable';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import { Reflector, CloudElement, Interface } from '../ReflectorData';
import { formatDateTimeUTC as centralizedFormatDateTimeUTC, getStatusVariant as centralizedGetStatusVariant } from '../../../helpers/formattersShapedData';
import CopyableValue from '@shared/components/CopyableValue';

// Data structure for Reflector Details Page
export interface ReflectorDetailItem {
  label: string;
  value?: string | number | boolean | React.ReactNode;
  type?: string;
  variant?: string;
}

export interface ReflectorDetailSection {
  header: string;
  isCollapsible?: boolean;
  initialExpanded?: boolean;
  items: ReflectorDetailItem[];
}

// Using centralized formatDateTimeUTC with legacy format
const formatDateTimeUTC = (dateString: string) => centralizedFormatDateTimeUTC(dateString, true);

// Function to build details sections from a reflector object
export function getReflectorDetailsSections(reflector?: Reflector): ReflectorDetailSection[] {
  const detailsSection: ReflectorDetailSection = {
    header: 'Details',
    items: [
      { label: 'ID', value: reflector?.name || '' },
      { label: 'Cluster', value: reflector?.cluster || '' },
      { label: 'Name', value: reflector?.description || '' },
      { label: 'Status', value: reflector?.status || '', type: 'tag', variant: getStatusVariant(reflector?.status) },
      { label: 'UUID', value: reflector?.subId || '' },
      { label: 'Version', value: reflector?.version || '' },
      { label: 'Tag', value: reflector?.tags || '' },
      { label: 'Last Sync', value: formatDateTimeUTC(reflector?.lastSync || '') },
      { label: 'Creation Date', value: formatDateTimeUTC(reflector?.createdAt || '') },
      { label: 'Cloud', value: reflector?.cloud || '', type: 'tag', variant: reflector?.cloud === 'AWS' ? 'warning' : reflector?.cloud === 'GCP' ? 'success' : 'neutral' },
      { label: 'VPC/VNET/VCN', value: reflector?.vpcId || '' },
      { label: 'Region', value: reflector?.region || '', type: 'tag', variant: 'neutral' },
      { label: 'License Type', value: reflector?.license?.type || '-' },
      { label: 'License ID', value: reflector?.license?.serial || '-' },
      { label: 'IAM Permissions', value: '', type: 'tag', variant: 'warning' },
    ],
  };

  const cloudElementsSection: ReflectorDetailSection = {
    header: 'Cloud Elements Discovered',
    items: [],
  };

  const ctxrInterfacesSection: ReflectorDetailSection = {
    header: 'Interfaces',
    items: [],
  };

  return [
    detailsSection,
    cloudElementsSection,
    ctxrInterfacesSection,
  ];
}

// Using centralized getStatusVariant
const getStatusVariant = centralizedGetStatusVariant;

export const cloudElementsColumns: GenericTableColumn<CloudElement>[] = [
  { header: 'Type', accessor: 'elementType' },
  { 
    header: 'Element ID', 
    accessor: 'elementId',
    renderCell: (row) => (
      <CopyableValue value={row.elementId || ''} />
    )
  },
  { 
    header: 'VPC', 
    accessor: 'vpcId',
    renderCell: (row) => (
      <CopyableValue value={row.vpcId || ''} />
    )
  },
  { header: 'Region', accessor: 'region' },
  { 
    header: 'Tags', 
    accessor: 'tags',
    renderCell: (row) => {
      if (!row.tags) return '-';
      try {
        // Parse the JSON string
        const tags = typeof row.tags === 'string' ? JSON.parse(row.tags) : row.tags;
        if (typeof tags === 'object' && tags !== null) {
          return (
            <div className="flex flex-wrap gap-1">
              {Object.entries(tags).map(([key, value]) => (
                <GenericTag 
                  key={key} 
                  label={`${key}: ${value}`} 
                  variant="neutral" 
                />
              ))}
            </div>
          );
        }
        return <span>{String(tags)}</span>;
      } catch (error) {
        // If JSON parsing fails, treat as comma-separated string
        const tagString = String(row.tags);
        if (tagString.includes(',')) {
          const tagArray = tagString.split(',').map(tag => tag.trim()).filter(tag => tag);
          return (
            <div className="flex flex-wrap gap-1">
              {tagArray.map((tag, index) => (
                <GenericTag 
                  key={index} 
                  label={tag} 
                  variant="neutral" 
                />
              ))}
            </div>
          );
        }
        return <GenericTag label={tagString} variant="neutral" />;
      }
    }
  },
  { header: 'Discovered At', accessor: 'discoveredAt', renderCell: (row) => formatDateTimeUTC(row.discoveredAt) },
];

export const ctxrInterfacesColumns: GenericTableColumn<Interface>[] = [
  { header: 'Interface', accessor: 'interface' },
  { 
    header: 'IP Address', 
    accessor: 'ipAddress',
    renderCell: (row) => {
      // ipAddress stays a comma-joined string so search and sort keep working;
      // render one address per line so IPv4 and IPv6 do not share a wide row.
      const ips = (row.ipAddress || '').split(',').map(ip => ip.trim()).filter(Boolean);
      if (ips.length <= 1) return <CopyableValue value={ips[0] || ''} />;
      return (
        <div className="flex flex-col gap-1">
          {ips.map(ip => (
            <CopyableValue key={ip} value={ip} />
          ))}
        </div>
      );
    }
  },
  { 
    header: 'MAC Address', 
    accessor: 'macAddress',
    renderCell: (row) => (
      <CopyableValue value={row.macAddress || ''} />
    )
  },
  { 
    header: 'VPC ID', 
    accessor: 'vpcId',
    renderCell: (row) => (
      <CopyableValue value={row.vpcId || ''} />
    )
  },
  { 
    header: 'Cloud Subnet ID', 
    accessor: 'cloudSubnetId',
    renderCell: (row) => (
      <CopyableValue value={row.cloudSubnetId || ''} />
    )
  },
];
